import { ChangeDetectorRef, Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AdminService } from 'src/app/service/admin.service';
import { ExportService } from 'src/app/service/export.service';
import { UserService } from 'src/app/service/user.service';
import { buildXlsxBlob } from 'src/app/utils/xlsx-export';
import { readXlsxSheet, XlsxReadError, XLSX_MAX_ROWS } from 'src/app/utils/xlsx-import';
import { isValidEmail, looksLikeEmail, normalizeHeader, pluralKey, stripWrappingQuotes } from 'src/app/utils/sheet-import-helpers';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';

/** Situação de uma linha da planilha depois de lida e confrontada com a base. */
export enum AuthorRowStatus {
  /** Já tem conta no JEMS (casou pelo e-mail). */
  EXISTING = 'existing',
  /** E-mail não encontrado — vira conta nova. */
  NEW = 'new',
  /** E-mail ausente ou malformado: a linha não tem chave, não dá pra decidir. */
  INVALID_EMAIL = 'invalid-email',
  /** E-mail repetido em linha anterior da MESMA planilha. */
  DUPLICATE = 'duplicate',
  /** Conta criada nesta sessão (inativa, e-mail de ativação disparado). Estado
   *  TERMINAL: uma vez aqui a linha não volta a ser selecionável. */
  CREATED = 'created',
  /** E-mail válido e único, mas nome/sobrenome ausente OU com cara de e-mail
   *  (dado provavelmente digitado na coluna errada). O backend EXIGE
   *  first_name/last_name (`UserSerializer`, `allow_blank: False`) — deixar
   *  como `NEW` prometeria uma criação que sempre falharia com 400. */
  INCOMPLETE = 'incomplete',
}

export interface AuthorRow {
  /** Número da linha na planilha (1 = cabeçalho), pra o usuário achar o erro. */
  sheetLine: number;
  firstName: string;
  lastName: string;
  /** E-mail como veio na planilha (preserva a caixa digitada). */
  email: string;
  /** Chave normalizada (minúscula) usada na comparação. */
  emailKey: string;
  status: AuthorRowStatus;
  /** Nome já cadastrado, quando `EXISTING` — deixa ver divergência com a planilha. */
  existingName?: string;
  /** `EXISTING` que casou por e-mail ALTERNATIVO, não pelo principal. O chair
   *  precisa saber: o endereço da planilha é um secundário daquela conta. */
  matchedBySecondary?: boolean;
  /** Marcada na coluna seletora. Só linhas `NEW` podem ser marcadas — as demais
   *  não têm conta a criar (ou não têm chave válida). */
  selected: boolean;
  /** Requisição de criação EM VOO pra esta linha especificamente (distinto do
   *  `isCreating` do componente, que cobre o lote inteiro). */
  creating?: boolean;
  /** Chave i18n do erro da última tentativa de criação, se houve. A linha
   *  CONTINUA selecionada nesse caso — status fica `NEW`, então "Criar novos
   *  usuários" tenta de novo só as que falharam, sem o chair remarcar nada. */
  createError?: string;
}

// Cabeçalho oficial do modelo distribuído pelo botão "Baixar planilha modelo".
const TEMPLATE_HEADERS = ['Nome - Contato', 'Sobrenome - Contato', 'E-mail - Contato'];

@Component({
  selector: 'app-authors-list',
  templateUrl: './authors-list.component.html',
  styleUrls: ['./authors-list.component.scss']
})
export class AuthorsListComponent {
  readonly icons = EVENT_ADMIN_ICONS;
  readonly Status = AuthorRowStatus;
  readonly maxRows = XLSX_MAX_ROWS;

  /** Nome do arquivo lido — cabeçalho da tabela de resultado. */
  fileName: string = null;
  rows: AuthorRow[] = [];
  /** Chave i18n do erro que impediu a leitura (null = sem erro). */
  error: string = null;
  isLoading = false;
  /** Criação em voo — vira `[loading]` da barra flutuante. */
  isCreating = false;
  /** Realce da zona de drop enquanto o arquivo é arrastado por cima. */
  isDragging = false;

  constructor(
    private adminService: AdminService,
    private userService: UserService,
    private exportService: ExportService,
    private cdr: ChangeDetectorRef
  ) { }

  // ============================================================
  // Resumo (cabeçalho da tabela)
  // ============================================================

  get total(): number { return this.rows.length; }
  get existingCount(): number { return this.countOf(AuthorRowStatus.EXISTING); }
  get newCount(): number { return this.countOf(AuthorRowStatus.NEW); }
  get createdCount(): number { return this.countOf(AuthorRowStatus.CREATED); }

  /** Exposto ao template; a regra mora em `sheet-import-helpers`. */
  pluralKey(base: string, count: number): string {
    return pluralKey(base, count);
  }

  /** Por que a linha caiu em `INCOMPLETE` — derivado on-the-fly (não guardado
   *  na linha) porque é puro function-of-state, sem custo de recomputar. */
  incompleteReasonKey(row: AuthorRow): string {
    const base = 'admin.event.submissions.authors-list.incomplete-reason.';
    if (looksLikeEmail(row.firstName) || looksLikeEmail(row.lastName)) {
      return base + 'looks-like-email';
    }
    return base + 'missing-name';
  }
  get problemCount(): number {
    return this.countOf(AuthorRowStatus.INVALID_EMAIL) + this.countOf(AuthorRowStatus.DUPLICATE)
      + this.countOf(AuthorRowStatus.INCOMPLETE);
  }

  private countOf(status: AuthorRowStatus): number {
    return this.rows.filter(r => r.status === status).length;
  }

  // ============================================================
  // Planilha modelo
  // ============================================================

  /**
   * Gera o .xlsx modelo com o cabeçalho oficial e uma linha de exemplo.
   *
   * O modelo é a forma mais barata de documentar o formato: o coordenador
   * baixa, preenche e devolve — e aí o cabeçalho chega exatamente como o
   * leitor espera, sem travessão/acento/ordem trocada.
   */
  async downloadTemplate(): Promise<void> {
    const rows = [{ values: ['Maria', 'Silva Andrade', 'maria.andrade@exemplo.br'] }];
    const blob = await buildXlsxBlob('Autores', TEMPLATE_HEADERS, rows, [], [22, 28, 38]);
    this.exportService.downloadBlob('jems-modelo-autores.xlsx', blob);
  }

  // ============================================================
  // Upload
  // ============================================================

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer && event.dataTransfer.files;
    if (files && files.length) { this.handleFile(files[0]); }
  }

  onFileSelected(input: HTMLInputElement): void {
    if (input.files && input.files.length) { this.handleFile(input.files[0]); }
    // Zera o input pra reenviar o MESMO arquivo dispare `change` de novo
    // (corrigido o Excel, o usuário reenvia sem trocar de arquivo).
    input.value = '';
  }

  clear(): void {
    this.fileName = null;
    this.rows = [];
    this.error = null;
  }

  private async handleFile(file: File): Promise<void> {
    this.error = null;
    this.rows = [];
    this.fileName = file.name;
    this.isLoading = true;

    try {
      const sheet = await readXlsxSheet(file);
      // Fica numa variável LOCAL, não em `this.rows`, até tudo resolver: a
      // tabela só aparece (via `*ngIf="rows.length"`) quando o confronto com a
      // base também já terminou. Sem isso a tabela piscava — surgia de
      // imediato com toda linha em "Novo" e só depois virava "Já cadastrado"
      // conforme a resposta do lookup chegava.
      const parsedRows = this.toAuthorRows(sheet);
      if (!parsedRows.length) {
        this.error = 'admin.event.submissions.authors-list.errors.empty';
        return;
      }
      await this.checkExisting(parsedRows);
      // Pré-marca todas as `NEW`: criar as contas novas é o caminho principal
      // da página, então o padrão poupa cliques. Só DEPOIS do confronto — antes
      // dele toda linha válida ainda está como `NEW`.
      parsedRows.filter(r => this.isSelectable(r)).forEach(r => r.selected = true);
      // Só agora — com leitura E confronto prontos — a tabela é revelada.
      this.rows = parsedRows;
    } catch (e) {
      this.error = e instanceof XlsxReadError
        ? `admin.event.submissions.authors-list.errors.${e.code}`
        : 'admin.event.submissions.authors-list.errors.read-failed';
    } finally {
      this.isLoading = false;
      // O fluxo é async fora de evento do template — sem isso a tabela só
      // aparece no próximo ciclo disparado por outra interação.
      this.cdr.markForCheck();
    }
  }

  // ============================================================
  // Cabeçalho e linhas
  // ============================================================

  /**
   * Descobre em que coluna estão nome, sobrenome e e-mail.
   *
   * Casa por PALAVRA-CHAVE, não pelo texto inteiro, pra tolerar sufixos como
   * "- Contato". "sobrenome" é testado ANTES de "nome" — senão a coluna
   * "Sobrenome" seria capturada pela regra de "nome" (é substring dela).
   */
  private mapColumns(header: string[]): { first: number, last: number, email: number } {
    const map = { first: -1, last: -1, email: -1 };

    header.forEach((cell, index) => {
      const h = normalizeHeader(cell);
      if (!h) { return; }
      if (map.email < 0 && (h.indexOf('mail') >= 0 || h.indexOf('e-mail') >= 0)) {
        map.email = index;
      } else if (map.last < 0 && h.indexOf('sobrenome') >= 0) {
        map.last = index;
      } else if (map.first < 0 && h.indexOf('nome') >= 0) {
        map.first = index;
      }
    });

    return map;
  }

  private toAuthorRows(sheet: string[][]): AuthorRow[] {
    if (!sheet.length) { return []; }

    const columns = this.mapColumns(sheet[0]);
    // Sem coluna de e-mail não há chave — é o engano mais provável do usuário
    // (planilha própria, fora do modelo), então merece mensagem específica.
    if (columns.email < 0) {
      throw new XlsxReadError('no-email-column');
    }

    const rows: AuthorRow[] = [];
    const seen = new Set<string>();

    // Começa em 1: a linha 0 é o cabeçalho.
    for (let i = 1; i < sheet.length; i++) {
      const cells = sheet[i];
      const at = (index: number) => (index >= 0 && cells[index] ? cells[index] : '');

      // Tira aspas envolventes ANTES de validar — planilha vinda de CSV é a
      // origem mais comum ("'joao@x.com'" chegaria como "Novo" e falharia na
      // criação, já que o Django rejeita aspas em e-mail).
      const email = stripWrappingQuotes(at(columns.email));
      const emailKey = email.toLowerCase();
      const firstName = at(columns.first);
      const lastName = at(columns.last);
      const row: AuthorRow = {
        sheetLine: i + 1,
        firstName,
        lastName,
        email,
        emailKey,
        status: AuthorRowStatus.NEW,
        selected: false,
      };

      if (!email || !isValidEmail(email)) {
        row.status = AuthorRowStatus.INVALID_EMAIL;
      } else if (seen.has(emailKey)) {
        row.status = AuthorRowStatus.DUPLICATE;
      } else {
        // Marca a chave AINDA que incompleta: se o mesmo e-mail aparecer nas
        // duas formas (sem nome aqui, completo depois), a segunda ocorrência
        // é DUPLICATE — dedupe é sobre identidade, não sobre a linha "boa".
        seen.add(emailKey);
        if (!firstName || !lastName || looksLikeEmail(firstName) || looksLikeEmail(lastName)) {
          row.status = AuthorRowStatus.INCOMPLETE;
        }
      }

      rows.push(row);
    }

    return rows;
  }

  // ============================================================
  // Seleção (coluna seletora)
  // ============================================================
  // Convenção do JEMS3 (`.jems3-table-selectable`): última coluna, checkbox
  // por linha + master no header, `.selected` na `<tr>` pro contorno accent.

  /** Só `NEW` é selecionável: é a única situação que gera criação de conta.
   *  `EXISTING` já tem conta; inválida/repetida não tem chave utilizável. */
  isSelectable(row: AuthorRow): boolean {
    return row.status === AuthorRowStatus.NEW;
  }

  get selectableRows(): AuthorRow[] {
    return this.rows.filter(r => this.isSelectable(r));
  }

  get selectedRows(): AuthorRow[] {
    return this.rows.filter(r => r.selected && this.isSelectable(r));
  }

  get selectedCount(): number {
    return this.selectedRows.length;
  }

  /** Master marcado = TODAS as selecionáveis estão marcadas (e existe ao menos
   *  uma). Com zero selecionáveis o master fica desmarcado e desabilitado. */
  allSelected(): boolean {
    const selectable = this.selectableRows;
    return selectable.length > 0 && selectable.every(r => r.selected);
  }

  someSelected(): boolean {
    const count = this.selectedCount;
    return count > 0 && count < this.selectableRows.length;
  }

  toggleAll(checked: boolean): void {
    this.selectableRows.forEach(r => r.selected = checked);
  }

  // ============================================================
  // Ações da barra flutuante
  // ============================================================

  /**
   * Cria as contas das linhas marcadas — UMA REQUISIÇÃO POR LINHA, em
   * sequência (decisão registrada: não é lote no backend). Cada requisição
   * atualiza SÓ a sua própria linha assim que resolve, então o chair vê o
   * progresso avançar item a item em vez de esperar um resultado só no fim.
   *
   * Conta nasce inativa; o backend dispara o e-mail de ativação padrão do
   * JEMS. Sem vínculo com o evento — só cria a conta no sistema.
   *
   * Falha em uma linha NÃO para as demais, e não desmarca a linha que
   * falhou: ela continua `NEW` + selecionada, então clicar em "Criar novos
   * usuários" de novo tenta só quem falhou.
   */
  async createSelected(): Promise<void> {
    const toCreate = this.selectedRows;
    if (!toCreate.length || this.isCreating) { return; }

    this.isCreating = true;
    const eventId = this.adminService.eventId;

    for (const row of toCreate) {
      row.creating = true;
      row.createError = null;
      this.cdr.markForCheck();

      try {
        await this.userService.createAuthor(eventId, {
          firstName: row.firstName, lastName: row.lastName, email: row.email
        }).toPromise();
        row.status = AuthorRowStatus.CREATED;
        row.selected = false;
      } catch (e) {
        row.createError = e instanceof HttpErrorResponse && e.status === 409
          ? 'admin.event.submissions.authors-list.errors.create-conflict'
          : 'admin.event.submissions.authors-list.errors.create-failed';
        // O interceptor HTTP global já mostra um snackbar pra todo erro de
        // rede (`ErrorService.handleError`) — não duplicamos aqui. O badge
        // na própria linha é o complemento LOCAL: qual autor, especificamente,
        // não foi criado.
      } finally {
        row.creating = false;
        this.cdr.markForCheck();
      }
    }

    this.isCreating = false;
    this.cdr.markForCheck();
  }

  // ============================================================
  // Confronto com a base
  // ============================================================

  /**
   * Marca quem já tem conta. Um único POST com todos os e-mails válidos —
   * a busca de usuário existente (`/core/user/search/`) não serve aqui: ela
   * aceita um termo por requisição e casa por substring, não por chave.
   *
   * O backend procura a chave nos TRÊS campos de e-mail do JEMS (principal +
   * os dois alternativos do Profile), como manda a regra de unicidade do
   * sistema; por isso a resposta diz qual campo casou.
   */
  private checkExisting(rows: AuthorRow[]): Promise<void> {
    const pending = rows.filter(r => r.status === AuthorRowStatus.NEW);
    if (!pending.length) { return Promise.resolve(); }

    const eventId = this.adminService.eventId;
    const emails = pending.map(r => r.emailKey);

    return new Promise(resolve => {
      this.userService.lookupAuthorsByEmail(eventId, emails).subscribe(found => {
        const byEmail = new Map<string, { name: string, secondary: boolean }>();
        (found || []).forEach(u => {
          const name = [u.firstName, u.lastName].filter(part => !!part).join(' ').trim();
          byEmail.set(u.requestedEmail, { name, secondary: u.matchedField !== 'email' });
        });

        pending.forEach(row => {
          const hit = byEmail.get(row.emailKey);
          if (hit) {
            row.status = AuthorRowStatus.EXISTING;
            row.existingName = hit.name || null;
            row.matchedBySecondary = hit.secondary;
          }
        });

        this.cdr.markForCheck();
        resolve();
      }, () => {
        // Falha no confronto não descarta a leitura: a tabela continua útil,
        // mas sem afirmar "novo" para quem talvez exista.
        this.error = 'admin.event.submissions.authors-list.errors.lookup-failed';
        this.cdr.markForCheck();
        resolve();
      });
    });
  }
}
